import toast from "react-hot-toast";
import { AxiosError } from "axios";
import { UseQueryOptions, useQuery } from "react-query";

import { getServerError } from "utilities/get-server-error";
import { ITimeZone, ITimeZonesGetQueryParameters, Route } from "@edgetier/watchtower-types";
import { getAllPaginatedItems } from "utilities/get-all-paginated-items";

// If the request fails default back to UTC.
export const DEFAULT_TIME_ZONE = "UTC";

/**
 * Request for getting all time-zones
 * @param url
 * @param queryParameters
 */
const requestTimeZones = async (url: string, queryParameters: ITimeZonesGetQueryParameters) => {
    const timeZones = await getAllPaginatedItems<ITimeZone>(url, { params: queryParameters });
    return timeZones;
};

/**
 * Get the time zone of the user or approximate it with a call to the backend. The standard method (using a browser API
 * method) seems well supported enough that the backend method could be deprecated really.
 * @param url Time zones URL.
 */
const getTimeZone = async (url: string) => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        const params: ITimeZonesGetQueryParameters = { limit: 1, offsetMinutes: -new Date().getTimezoneOffset() };
        const response = await requestTimeZones(url, params);
        if (response.length === 0) {
            toast.error("Could not find your time zone. Dates and times may be not as you expect.");
            return DEFAULT_TIME_ZONE;
        }
        return response[0].timeZone;
    }
};

/**
 * Get the approximate time zone of the user.
 * @param options       React Query options.
 * @returns             React Query result.
 */
export const useTimeZone = (options: UseQueryOptions<string, AxiosError<void>> = {}) => {
    return useQuery<string, AxiosError<void>>(Route.TimeZones, () => getTimeZone(Route.TimeZones), {
        onError: (serverError: AxiosError<any>) => {
            toast.error(getServerError(serverError));
        },
        cacheTime: Infinity,
        staleTime: Infinity,
        ...options,
    });
};

/**
 * Get all time-zones from API
 * @param queryParameters
 * @param options
 */
export const useTimeZones = <T = ITimeZone[]>(
    queryParameters: ITimeZonesGetQueryParameters = {},
    options: Omit<UseQueryOptions<ITimeZone[], AxiosError, T>, "queryKey" | "queryFn"> = {}
) => {
    const url = Route.TimeZones;
    return useQuery<ITimeZone[], AxiosError, T>(
        [url, queryParameters],
        () => requestTimeZones(url, queryParameters),
        options
    );
};
